import { MODULE_CATALOGUE, modulePermission, type ModuleId } from './modules';
import { ROLE_CATEGORY_IDS, type RoleCategoryId } from './role-categories';
import {
  MIDDLEWARE_MODULES,
  MIDDLEWARE_MODULE_ACTION_PERMISSIONS,
  MIDDLEWARE_MODULE_PERMISSIONS,
  findMiddlewareModuleByPermission,
  isModuleActionPermission,
  type MiddlewareModuleActionPermission,
  type MiddlewareModulePermission,
} from './middlewares';

/** Fixed admin permissions (brief §6.1). Module permissions are derived from the catalogue. */
export const ADMIN_PERMISSIONS = {
  'users.read': 'View users',
  'users.invite': 'Invite users',
  'users.assign_roles': 'Assign roles ranked below your own',
  'roles.create': 'Create new roles',
  'roles.edit': 'Edit role names, ranks and permissions',
  'roles.delete': 'Delete custom roles',
  'branding.manage': 'Change school name, logo and colours',
  'auth_providers.manage': 'Configure login methods and email server',
  'modules.manage': 'Turn modules on and off, and reorder them',
  'push.test': 'Send test notifications',
  'notifications.manage': 'Set how long archived notifications are kept',
  'audit.read': 'View the audit log',
  'users.impersonate': 'View the app as another user (view only, time-limited, audited)',
  // Developer tools (2026-10-26; = DEV_VIEW_AS_PERMISSION): offered only while the server's
  // TP_DEV_VIEW_AS is on.
  'dev.view_as': 'Developer: view modules as another email (testing; never on LIVE)',
} as const;

export type AdminPermission = keyof typeof ADMIN_PERMISSIONS;

/**
 * School-wide critical permissions that only the seeded tenant_admin role may hold
 * (owner decision 2026-10-06). They can never be granted to Local Admin or any custom role.
 * roles.edit is deliberately NOT here: lower admins may edit roles ranked below their own.
 */
export const TENANT_ADMIN_ONLY_PERMISSIONS: readonly AdminPermission[] = [
  'branding.manage',
  'auth_providers.manage',
  'modules.manage',
  'roles.create',
  'roles.delete',
  'users.impersonate',
  'push.test',
  'notifications.manage',
  'dev.view_as',
];

export function isTenantAdminOnly(permission: string): boolean {
  return (TENANT_ADMIN_ONLY_PERMISSIONS as readonly string[]).includes(permission);
}
export type ModulePermission = `module.${ModuleId}.use`;
/** Middleware modules add `module.<middleware>.<module>.use` (middlewares.ts). */
export type Permission =
  | AdminPermission
  | ModulePermission
  | MiddlewareModulePermission
  | MiddlewareModuleActionPermission;

/** Admin permissions that only read. Every other admin permission changes something. */
export const READ_ONLY_ADMIN_PERMISSIONS: readonly AdminPermission[] = ['users.read', 'audit.read'];

/**
 * A write permission changes school data: every admin permission except the read-only ones,
 * and every module whose catalogue entry isn't readOnlySafe. The Users category never holds
 * one (decision 2026-10-10 #5; widened from "the User role never writes").
 */
export function isWritePermission(permission: string): boolean {
  // Declared module actions are privileged: never the Users category.
  if (isModuleActionPermission(permission)) return true;
  if (permission in ADMIN_PERMISSIONS) {
    return !(READ_ONLY_ADMIN_PERMISSIONS as readonly string[]).includes(permission);
  }
  const module =
    MODULE_CATALOGUE.find((m) => modulePermission(m.id) === permission) ??
    findMiddlewareModuleByPermission(permission);
  return module ? !module.readOnlySafe : true;
}

const SLT_AND_ABOVE: readonly RoleCategoryId[] = ['slt', 'local_admin', 'tenant_admin'];
const LOCAL_ADMIN_AND_ABOVE: readonly RoleCategoryId[] = ['local_admin', 'tenant_admin'];
const TENANT_ADMIN_CATEGORY: readonly RoleCategoryId[] = ['tenant_admin'];

/**
 * Category gating (decision 2026-10-10 #5): which role categories may hold a permission.
 * Permissions not listed here are open to every category (subject to the write rule).
 * Module entries can be added here as `module.<id>.use` when a module is limited to some
 * categories. Table in docs/DECISIONS.md (2026-10-10 role categories).
 */
export const PERMISSION_ALLOWED_CATEGORIES: Readonly<
  Partial<Record<Permission, readonly RoleCategoryId[]>>
> = {
  'users.read': SLT_AND_ABOVE,
  'users.invite': SLT_AND_ABOVE,
  'users.assign_roles': SLT_AND_ABOVE,
  'audit.read': SLT_AND_ABOVE,
  'roles.edit': LOCAL_ADMIN_AND_ABOVE,
  ...Object.fromEntries(
    TENANT_ADMIN_ONLY_PERMISSIONS.map((p) => [p, TENANT_ADMIN_CATEGORY] as const),
  ),
  ...Object.fromEntries(MIDDLEWARE_MODULES.map((m) => [m.permission, m.categories] as const)),
  ...Object.fromEntries(
    MIDDLEWARE_MODULES.flatMap((m) =>
      m.actionPermissions.map((a) => [a.permission, m.categories] as const),
    ),
  ),
};

/** Categories that may hold the permission: the gating table, minus Users for writes. */
export function allowedCategoriesFor(permission: string): RoleCategoryId[] {
  const gated = PERMISSION_ALLOWED_CATEGORIES[permission as Permission] ?? ROLE_CATEGORY_IDS;
  return gated.filter((c) => c !== 'users' || !isWritePermission(permission));
}

export function isPermissionAllowedForCategory(
  permission: string,
  category: RoleCategoryId,
): boolean {
  return allowedCategoriesFor(permission).includes(category);
}

/** Requested permissions a role of this category may not hold (→ 400 on the server). */
export function categoryPermissionViolations(
  category: RoleCategoryId,
  permissions: readonly string[],
): string[] {
  return [...new Set(permissions)].filter((p) => !isPermissionAllowedForCategory(p, category));
}

export interface PermissionInfo {
  id: Permission;
  group: 'admin' | 'module';
  description: string;
}

export const MODULE_PERMISSIONS: readonly ModulePermission[] = MODULE_CATALOGUE.map(
  (m) => modulePermission(m.id) as ModulePermission,
);

export const ALL_PERMISSIONS: readonly Permission[] = [
  ...(Object.keys(ADMIN_PERMISSIONS) as AdminPermission[]),
  ...MODULE_PERMISSIONS,
  ...MIDDLEWARE_MODULE_PERMISSIONS,
  ...MIDDLEWARE_MODULE_ACTION_PERMISSIONS,
];

/**
 * Permission categories group the Roles & permissions page (super-groups: school
 * administration, then one category per module). Every admin permission must map to an
 * admin category — the Record type makes a missing mapping a compile error.
 */
export const ADMIN_PERMISSION_CATEGORIES = [
  { id: 'people', title: 'People', description: 'See, invite and manage staff accounts' },
  { id: 'roles', title: 'Roles & access', description: 'Define roles and what each role can do' },
  {
    id: 'school',
    title: 'School setup',
    description: 'Branding, login methods and which modules are on',
  },
  {
    id: 'communication',
    title: 'Notifications',
    description: 'Test notifications and set how long archived ones are kept',
  },
  { id: 'oversight', title: 'Audit & oversight', description: 'Review who changed what' },
  {
    id: 'developer',
    title: 'Developer tools',
    description: 'Testing aids for DEMO and development servers (never on LIVE)',
  },
] as const;

export type AdminCategoryId = (typeof ADMIN_PERMISSION_CATEGORIES)[number]['id'];

export const ADMIN_PERMISSION_CATEGORY: Record<AdminPermission, AdminCategoryId> = {
  'users.read': 'people',
  'users.invite': 'people',
  'users.assign_roles': 'people',
  'users.impersonate': 'people',
  'roles.create': 'roles',
  'roles.edit': 'roles',
  'roles.delete': 'roles',
  'branding.manage': 'school',
  'auth_providers.manage': 'school',
  'modules.manage': 'school',
  'push.test': 'communication',
  'notifications.manage': 'communication',
  'audit.read': 'oversight',
  'dev.view_as': 'developer',
};

export type PermissionGroup = 'admin' | 'module';

export interface PermissionCategory {
  /** 'people', 'school', … or 'module:<moduleId>' */
  id: string;
  group: PermissionGroup;
  title: string;
  description: string;
  order: number;
}

export interface PermissionInfo {
  id: Permission;
  group: PermissionGroup;
  category: string;
  description: string;
  /** Shown locked ("Tenant Admin only") when editing any other role. */
  tenantAdminOnly?: boolean;
  /** Role categories that may hold it (absent from servers before 2026-10-10 = all). */
  allowedCategories?: RoleCategoryId[];
}

export function moduleCategoryId(moduleId: string): `module:${string}` {
  return `module:${moduleId}`;
}

export const PERMISSION_CATEGORIES: readonly PermissionCategory[] = [
  ...ADMIN_PERMISSION_CATEGORIES.map((c, i) => ({
    ...c,
    group: 'admin' as const,
    order: 10 + i * 10,
  })),
  ...MODULE_CATALOGUE.map((m, i) => ({
    id: moduleCategoryId(m.id),
    group: 'module' as const,
    title: m.title,
    description: `Access to ${m.title}`,
    order: 1000 + i * 10,
  })),
  ...MIDDLEWARE_MODULES.map((m, i) => ({
    id: moduleCategoryId(m.id),
    group: 'module' as const,
    title: m.fullTitle,
    description: `Access to ${m.fullTitle}`,
    order: 2000 + i * 10,
  })),
];

/**
 * Catalogue served by GET /api/v1/admin/permissions. Future module permissions
 * (e.g. module.attendance.take) are added here with category moduleCategoryId('attendance').
 */
export const PERMISSION_CATALOGUE: readonly PermissionInfo[] = [
  ...(Object.entries(ADMIN_PERMISSIONS) as [AdminPermission, string][]).map(
    ([id, description]) => ({
      id,
      group: 'admin' as const,
      category: ADMIN_PERMISSION_CATEGORY[id],
      description,
      tenantAdminOnly: isTenantAdminOnly(id),
      allowedCategories: allowedCategoriesFor(id),
    }),
  ),
  ...MODULE_CATALOGUE.map((m) => ({
    id: modulePermission(m.id) as ModulePermission,
    group: 'module' as const,
    category: moduleCategoryId(m.id),
    description: `Use ${m.title}`,
    allowedCategories: allowedCategoriesFor(modulePermission(m.id)),
  })),
  ...MIDDLEWARE_MODULES.flatMap((m) => [
    {
      id: m.permission,
      group: 'module' as const,
      category: moduleCategoryId(m.id),
      description: `Use ${m.fullTitle}`,
      allowedCategories: allowedCategoriesFor(m.permission),
    },
    ...m.actionPermissions.map((a) => ({
      id: a.permission,
      group: 'module' as const,
      category: moduleCategoryId(m.id),
      description: a.label,
      allowedCategories: allowedCategoriesFor(a.permission),
    })),
  ]),
];

export interface PermissionSection {
  category: PermissionCategory;
  permissions: PermissionInfo[];
}

/**
 * Groups a permission catalogue into ordered sections for the Roles page. Empty categories
 * are dropped; permissions whose category is unknown go to a trailing "Other" section so a
 * newer server never hides permissions from an older app.
 */
export function groupPermissions(
  catalogue: readonly PermissionInfo[],
  categories: readonly PermissionCategory[] = PERMISSION_CATEGORIES,
): PermissionSection[] {
  const known = new Set(categories.map((c) => c.id));
  const sections = categories
    .slice()
    .sort((a, b) => a.order - b.order)
    .map((category) => ({
      category,
      permissions: catalogue.filter((p) => p.category === category.id),
    }))
    .filter((s) => s.permissions.length > 0);
  const orphans = catalogue.filter((p) => !known.has(p.category));
  if (orphans.length === 0) return sections;
  const other: PermissionCategory = {
    id: 'other',
    group: 'admin',
    title: 'Other',
    description: 'Uncategorised permissions',
    order: Number.MAX_SAFE_INTEGER,
  };
  return [...sections, { category: other, permissions: orphans }];
}

/**
 * Roles page shows only modules the school has switched on (owner request). Module
 * categories for disabled modules are hidden; admin categories and "Other" always show.
 */
export function sectionsForEnabledModules(
  sections: readonly PermissionSection[],
  enabledModuleIds: ReadonlySet<string>,
): PermissionSection[] {
  return sections.filter((s) => {
    if (s.category.group !== 'module' || !s.category.id.startsWith('module:')) return true;
    return enabledModuleIds.has(s.category.id.slice('module:'.length));
  });
}

/**
 * Merges the editor's visible choices with permissions the role already holds but the
 * editor can't see (disabled modules), so saving never silently removes hidden grants.
 */
export function mergeHiddenGrants(
  existing: readonly string[],
  visibleIds: ReadonlySet<string>,
  chosen: readonly string[],
): string[] {
  const hidden = existing.filter((p) => !visibleIds.has(p));
  return [...new Set([...chosen, ...hidden])];
}

/** Section header summary, e.g. "3 of 4 on". */
export function sectionSummary(
  section: PermissionSection,
  granted: ReadonlySet<string>,
): { on: number; total: number } {
  return {
    on: section.permissions.filter((p) => granted.has(p.id)).length,
    total: section.permissions.length,
  };
}

/**
 * Permission IDs replaced by finer ones. Stored roles holding a legacy ID are expanded on
 * read/migration so nothing is lost (roles.manage split into create/edit/delete, 2026-10-06).
 */
export const LEGACY_PERMISSION_ALIASES: Readonly<Record<string, readonly Permission[]>> = {
  'roles.manage': ['roles.create', 'roles.edit', 'roles.delete'],
};

export function expandLegacyPermissions(ids: readonly string[]): string[] {
  return [
    ...new Set(ids.flatMap((id): readonly string[] => LEGACY_PERMISSION_ALIASES[id] ?? [id])),
  ];
}

export function isPermission(value: string): value is Permission {
  return (ALL_PERMISSIONS as readonly string[]).includes(value);
}

/** The Admin tab is shown when the user holds at least one admin permission. */
export function hasAnyAdminPermission(held: Iterable<string>): boolean {
  const adminIds = new Set<string>(Object.keys(ADMIN_PERMISSIONS));
  for (const p of held) if (adminIds.has(p)) return true;
  return false;
}
